/**
 * Created with IntelliJ IDEA.
 * User: tschenkel
 * Date: 3/19/14
 * Time: 4:43 PM
 * To change this template use File | Settings | File Templates.
 */

import { ILocalStorage } from "../localStorage/api/ILocalStorage";
import { LocalStorageFactory } from "../localStorage/factory/LocalStorageFactory";

import { ExternalInterface } from "@goodgamestudios/createjs-utilities";

/**
 * Abstract class used to provide some friendly API to deal with <code>SharedObjects</code>.
 */
export class AbstractCookie {

    /**
     * Wrapper for data stored on local storage.
     */
    protected _localStorage: ILocalStorage;

    /**
     * Enabled when local storage is initialized.
     */
    protected _initialized: boolean = false;

    /**
     * Constructor.
     */
    constructor(section: string) {
        if ((section === undefined) || (section === null) || (section === "")) {
            throw new Error("Section cannot be null or empty");
        }

        this._localStorage = LocalStorageFactory.createLocalStorage(section);
    }

    /**
     * Initialize method.
     *
     * <p>When the class is initialized, the template method <code>readLocalStorage</code> is called,
     * so you can <code>override</code> it if you would like to read the parameters stored in your cookie.</p>
     */
    public initialize(): Promise<any> {
        return new Promise((resolve, reject) => {
            if (this._initialized) {
                resolve();
                return;
            }

            this._localStorage.read()
                .then(result => {
                    this._initialized = true;
                    this.readLocalStorage();
                    resolve();
                })
                .catch(reason => {
                    this._initialized = true;
                    this.readLocalStorage();
                    resolve();
                });
        });
    }

    /**
     * Template method, called when the <code>AbstractCookie</code> was initialized.
     */
    protected readLocalStorage(): void {
        return;
    }

    /**
     * Store data on local storage.
     */
    protected writeLocalStorage(): void {
        this._localStorage.write();
    }

    /**
     * Throws an error when local storage is not initialized.
     */
    protected isInitialized(): void {
        if (!this._initialized) {
            throw new Error("Local Storage is not initialized.");
        }
    }

    /**
     * Method that write a new attribute on the object data of the cookie.
     *
     * <p>Use the parameter <code>writeNow</code> if you would like to persist the changes directly on the hard drive.</p>
     *
     * @param attributeName Name of the attribute that will be saved.
     * @param value Value of the attribute that will be saved.
     */
    public setAttribute(attributeName: string, value: any, writeNow: boolean = true): void {
        // Ensure that local storage is initialized
        this.isInitialized();

        // Save attribute on local storage
        this._localStorage.setAttribute(attributeName, value);

        if (writeNow) {
            this.writeLocalStorage();
        }
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>undefined</code> otherwise.
     */
    public getAttribute(attributeName: string): any {
        // Ensure that local storage is initialized
        this.isInitialized();

        // Return value from local storage
        return this._localStorage.getAttribute(attributeName);
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie and convert it to <code>String</code>.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>null</code> otherwise.
     */
    public getAttributeAsString(attributeName: string): string {
        // Ensure that local storage is initialized
        this.isInitialized();

        let value: any = this.getAttribute(attributeName);

        return String(value !== undefined ? value : "");
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie and convert it to <code>Boolean</code>.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>null</code> otherwise.
     */
    public getAttributeAsBoolean(attributeName: string): boolean {
        // Ensure that local storage is initialized
        this.isInitialized();

        let value: string = this.getAttributeAsString(attributeName);
        let valueAsBoolean: boolean = false;

        if (!isNaN(Number(value))) {
            valueAsBoolean = value !== "0";
        } else {
            valueAsBoolean = value.toLowerCase() === "true";
        }

        return valueAsBoolean;
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie and convert it to <code>int</code>.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>null</code> otherwise.
     */
    public getAttributeAsInt(attributeName: string): number {
        // Ensure that local storage is initialized
        this.isInitialized();

        return this.getAttributeAsNumber(attributeName);
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie and convert it to <code>uint</code>.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>null</code> otherwise.
     */
    public getAttributeAsUint(attributeName: string): number {
        // Ensure that local storage is initialized
        this.isInitialized();

        return this.getAttributeAsNumber(attributeName);
    }

    /**
     * Read the value of one attribute that is defined on the object data of the cookie and convert it to <code>Number</code>.
     *
     * <p>If no value was found, a <code>null</code> reference is returned.</p>
     *
     * @param attributeName Name of attribute that will be recovered.
     * @return The value of the attribute if was found, or <code>null</code> otherwise.
     */
    public getAttributeAsNumber(attributeName: string): number {
        // Ensure that local storage is initialized
        this.isInitialized();

        return Number(this.getAttribute(attributeName));
    }

    /**
     * Remove the attribute from the object data of the cookie.
     * @param attributeName Name of the attribute that will be removed.
     */
    public deleteAttribute(attributeName: string, writeNow: boolean = true): void {
        // Ensure that local storage is initialized
        this.isInitialized();

        if (this.hasAttribute(attributeName)) {
            this._localStorage.deleteAttribute(attributeName);

            if (writeNow) {
                this.writeLocalStorage();
            }
        }
    }

    /**
     * Verify if the attribute is defined in the data object of the cookie.
     *
     * @param attributeName Name of the attribute to look for.
     * @return <code>true</code> if the attribute was found and <code>false</code> otherwise.
     */
    public hasAttribute(attributeName: string): boolean {
        // Ensure that local storage is initialized
        this.isInitialized();

        return this._localStorage.hasAttribute(attributeName);
    }

    /**
     * Return a list of all attributes managed by this cookie.
     *
     * @returns a array of all attributes managed by this cookie.
     */
    public getAttributes(): string[] {
        // Ensure that local storage is initialized
        this.isInitialized();

        return this._localStorage.getAttributes();
    }
}
